import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError, QueryStatus } from "@reduxjs/toolkit/query";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

export enum CrudAction {
  CREATE = "CREATE",
  READ = "READ",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
}

export interface CrudHelper {
  state: CrudHelperState;
  messages: CrudMessages;
  status: CrudStatus;
}

type State<T> = [T, Dispatch<SetStateAction<T>>];

interface CrudHelperState {
  lastActionState: State<CrudAction | undefined>;
  actionResultState: State<Record<CrudAction, CrudActionResult>>;
}

interface CrudMessages {
  failure?: string;
  hint?: string;
  success?: string;
}

interface CrudStatus {
  pendingAction?: CrudAction;

  isMutating: boolean;
  isQuerying: boolean;
}

interface CrudActionResult<T = unknown> {
  status: QueryStatus;
  error?: FetchBaseQueryError | SerializedError;
  data?: T;
}

export function useCrudHelper(): CrudHelper {
  const [lastAction, setLastAction] = useState<CrudAction>();
  const [actionResults, setActionResults] = useState({} as Record<CrudAction, CrudActionResult>);

  const pendingAction = useMemo(
    () => getPendingAction(actionResults, lastAction),
    [actionResults, lastAction],
  );

  const messages = useMemo(
    () => createCrudMessages(actionResults, lastAction),
    [actionResults, lastAction],
  );

  return {
    state: {
      lastActionState: [lastAction, setLastAction],
      actionResultState: [actionResults, setActionResults],
    },
    messages,
    status: {
      pendingAction,
      isMutating: !!pendingAction && pendingAction !== CrudAction.READ,
      isQuerying: pendingAction === CrudAction.READ,
    },
  };
}

export function useQuery<T extends CrudActionResult>(state: CrudHelperState, queryResult: T): T {
  const { lastActionState: [, setLastAction], actionResultState: [, setActionResults] } = state;
  useEffect(
    () => putActionResult(setActionResults, CrudAction.READ, queryResult),
    [queryResult, setActionResults],
  );

  useEffect(
    () => {
      if (queryResult.status === QueryStatus.pending) {
        setLastAction(CrudAction.READ);
      }
    },
    [queryResult.status, setLastAction],
  );

  return queryResult;
}

export function useMutation<T extends readonly [unknown, R], R extends CrudActionResult>(state: CrudHelperState, action: CrudAction, mutation: T): T {
  const { lastActionState: [, setLastAction], actionResultState: [, setActionResults] } = state;
  const [, mutationResult] = mutation;
  useEffect(
    () => putActionResult(setActionResults, action, mutationResult),
    [action, mutationResult, setActionResults],
  );

  useEffect(
    () => {
      if (mutationResult.status === QueryStatus.pending) {
        setLastAction(action);
      }
    },
    [action, mutationResult.status, setLastAction],
  );

  return mutation;
}

function getPendingAction(actionResults: Record<CrudAction, CrudActionResult>, lastAction: CrudAction | undefined): CrudAction | undefined {
  const result = getActionResult(actionResults, lastAction);
  if (result?.status === QueryStatus.pending) {
    return lastAction;
  }

  return undefined;
}

const CLIENT_ERROR_TEXT = "Your input is invalid.";
const SERVER_ERROR_TEXT = "Oops. An unexpected server error happened.";

type TextLookup = Partial<Record<CrudAction, Partial<Record<QueryStatus, string>>>>;
const defaultTextLookup: TextLookup = {
  [CrudAction.CREATE]: {
    [QueryStatus.pending]: "Creating the item on the server...",
    [QueryStatus.fulfilled]: "Item has been created.",
  },
  [CrudAction.UPDATE]: {
    [QueryStatus.pending]: "Saving changes on the server...",
    [QueryStatus.fulfilled]: "Changes have been saved.",
  },
  [CrudAction.DELETE]: {
    [QueryStatus.pending]: "Deleting the item on the server...",
    [QueryStatus.fulfilled]: "Item has been deleted.",
  },
};

function createCrudMessages(actionResults: Record<CrudAction, CrudActionResult>, lastAction: CrudAction | undefined): CrudMessages {
  const result = getActionResult(actionResults, lastAction);
  if (lastAction === undefined || result === undefined) {
    return {};
  }

  if (result.status === QueryStatus.pending) {
    return {
      hint: defaultTextLookup[lastAction]
        ?.[QueryStatus.pending],
    };
  }

  if (result.status === QueryStatus.fulfilled) {
    return {
      success: defaultTextLookup[lastAction]
        ?.[QueryStatus.fulfilled],
    };
  }

  if (result.status === QueryStatus.rejected) {
    console.log(`CRUD error during ${lastAction}:`, result.error);
    if (result.error && "status" in result.error && typeof result.error.status === "number") {
      const failure = result.error.status === 400
        ? CLIENT_ERROR_TEXT
        : SERVER_ERROR_TEXT;

      return { failure };
    }

    if (lastAction !== CrudAction.READ) {
      return {
        failure: "Unexpected error.",
      };
    }
  }

  return {};
}

function getActionResult(actionResults: Record<CrudAction, CrudActionResult>, lastAction: CrudAction | undefined): CrudActionResult | undefined {
  return lastAction && actionResults[lastAction];
}

function putActionResult(setActionResults: Dispatch<SetStateAction<Record<CrudAction, CrudActionResult>>>, key: CrudAction, value: CrudActionResult) {
  setActionResults((prev) => ({
    ...prev, [key]: value,
  }));
}
